import { useState } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { XCircle, Loader2, Wand2 } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Magic sign-in interstitial. The token is only consumed on an explicit click
 * (POST), so email-scanner prefetches can't burn the link.
 */
export default function MagicLoginPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const { refresh } = useAuth();
  const token = params.get("token");
  const urlError = params.get("error");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(
    urlError === "expired"
      ? "This sign-in link has expired - links are valid for 15 minutes."
      : urlError === "used"
        ? "This sign-in link has already been used - each link works once."
        : !token && !urlError
          ? "This sign-in link is missing its token."
          : ""
  );

  const confirm = async () => {
    setLoading(true);
    try {
      const res = await axios.post(`${API}/auth/magic/verify`, { token }, { withCredentials: true });
      await refresh();
      navigate(res?.data?.is_new_user ? "/welcome" : "/dashboard", { replace: true });
    } catch (e) {
      setError(formatApiErrorDetail(e?.response?.data?.detail) || "This sign-in link didn't work.");
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" data-testid="magic-login-page">
      <div className="w-full max-w-md text-center">
        <div className="inline-flex items-center gap-2 mb-6">
          <BrandLogo size={28} />
          <span className="font-bold text-lg">PassADC</span>
        </div>
        {!error ? (
          <div className="glass rounded-2xl p-8 border border-border" data-testid="magic-login-confirm">
            <div className="w-12 h-12 rounded-xl bg-primary/15 flex items-center justify-center mx-auto mb-4">
              <Wand2 className="w-6 h-6 text-primary" />
            </div>
            <p className="font-semibold mb-1">You're one tap away</p>
            <p className="text-sm text-muted-foreground mb-6">Confirm below to sign in to your PassADC account.</p>
            <Button
              data-testid="magic-login-continue-btn"
              onClick={confirm}
              disabled={loading}
              className="w-full rounded-full py-6 text-base font-semibold"
            >
              {loading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing you in...</>) : "Continue to PassADC"}
            </Button>
          </div>
        ) : (
          <div className="glass rounded-2xl p-8 border border-destructive/30" data-testid="magic-login-error">
            <XCircle className="w-8 h-8 text-destructive mx-auto mb-4" />
            <p className="font-semibold mb-1">Sign-in link problem</p>
            <p className="text-sm text-muted-foreground mb-5">{error} Request a fresh one from the login page.</p>
            <Link to="/login" className="text-primary font-medium hover:underline text-sm" data-testid="magic-login-back">
              Back to sign in
            </Link>
          </div>
        )}
      </div>
    </div>
  );
}
